import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  IconButton,
  Typography,
  CircularProgress,
  Autocomplete,
  Chip
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ko } from 'date-fns/locale';
import { UserRecord, UserFormData, ASSIGNMENT_OPTIONS } from '../types';
import { createUser, updateUser, updateUserFile, getUser as getUserById } from '../services/userService';
import { uploadFile } from '../services/storageService';
import { useErrorHandler } from '../contexts/AppContext';
import ImageUpload from './ImageUpload';
import DocumentUpload from './DocumentUpload';

interface UserFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (user: UserRecord) => void;
  editUser?: UserRecord | null;
}

const UserForm: React.FC<UserFormProps> = ({
  open,
  onClose,
  onSuccess,
  editUser
}) => {
  const [formData, setFormData] = useState<UserFormData>({
    headquarters: '',
    department: '',
    position: '',
    role: '',
    assignments: [],
    location: '',
    name: '',
    email: '',
    employeeId: '',
    appointmentPeriod: {
      start: new Date(),
      end: null
    },
    remarks: '',
    initialEducationDate: null,
    recentEducationDate: null
  });

  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [tempUser, setTempUser] = useState<UserRecord | null>(null);

  const handleError = useErrorHandler();
  const isEdit = !!editUser;

  // editUser ID가 변경될 때만 폼 데이터 초기화 (문서 업데이트로 인한 재설정 방지)
  const [lastEditUserId, setLastEditUserId] = useState<string | null>(null);
  
  useEffect(() => {
    const currentEditUserId = editUser?.id || null;
    
    // 다른 사용자로 변경되거나 새로 열린 경우에만 폼 초기화
    if (currentEditUserId !== lastEditUserId) {
      setLastEditUserId(currentEditUserId);
      
      if (editUser) {
        setFormData({
          headquarters: editUser.headquarters || '',
          department: editUser.department || '',
          position: editUser.position || '',
          role: editUser.role || '',
          assignments: editUser.assignments || [],
          location: editUser.location || '',
          name: editUser.name || '',
          email: editUser.email || '',
          employeeId: editUser.employeeId || '',
          appointmentPeriod: {
            start: editUser.appointmentPeriod?.start ? 
              (editUser.appointmentPeriod.start as any).toDate ? 
                (editUser.appointmentPeriod.start as any).toDate() : 
                (editUser.appointmentPeriod.start as any).seconds ? 
                  new Date((editUser.appointmentPeriod.start as any).seconds * 1000) : 
                  new Date() : new Date(),
            end: editUser.appointmentPeriod?.end ? 
              (editUser.appointmentPeriod.end as any).toDate ? 
                (editUser.appointmentPeriod.end as any).toDate() : 
                (editUser.appointmentPeriod.end as any).seconds ? 
                  new Date((editUser.appointmentPeriod.end as any).seconds * 1000) : 
                  null : null
          },
          remarks: editUser.remarks || '',
          initialEducationDate: editUser.initialEducationDate ? 
            (editUser.initialEducationDate as any).toDate ? 
              (editUser.initialEducationDate as any).toDate() : 
              (editUser.initialEducationDate as any).seconds ? 
                new Date((editUser.initialEducationDate as any).seconds * 1000) : 
                null : null,
          recentEducationDate: editUser.recentEducationDate ? 
            (editUser.recentEducationDate as any).toDate ? 
              (editUser.recentEducationDate as any).toDate() : 
              (editUser.recentEducationDate as any).seconds ? 
                new Date((editUser.recentEducationDate as any).seconds * 1000) : 
                null : null
        });
        setPhotoPreview(editUser.photo?.url || null);
      } else {
        // 새 사용자 등록인 경우 폼 초기화
        setFormData({
          headquarters: '',
          department: '',
          position: '',
          role: '',
          assignments: [],
          location: '',
          name: '',
          email: '',
          employeeId: '',
          appointmentPeriod: {
            start: new Date(),
            end: null
          },
          remarks: '',
          initialEducationDate: null,
          recentEducationDate: null
        });
        setPhotoPreview(null);
      }
      setPhotoFile(null);
      setErrors({});
      setTempUser(null);
    }
  }, [editUser, lastEditUserId]);

  // 입력값 변경 핸들러
  const handleInputChange = (field: keyof UserFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // 에러 클리어
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // 날짜 변경 핸들러
  const handleDateChange = (field: 'start' | 'end', date: Date | null) => {
    setFormData(prev => ({
      ...prev,
      appointmentPeriod: {
        ...prev.appointmentPeriod,
        [field]: date
      }
    }));
  };

  // 이미지 선택 핸들러
  const handleImageSelect = (file: File, preview: string) => {
    setPhotoFile(file);
    setPhotoPreview(preview);
    setErrors(prev => ({
      ...prev,
      photo: ''
    }));
  };

  // 폼 유효성 검증
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = '이름을 입력해주세요.';
    }

    if (!formData.employeeId.trim()) {
      newErrors.employeeId = '사번을 입력해주세요.';
    }

    if (!formData.headquarters.trim()) {
      newErrors.headquarters = '본부를 입력해주세요.';
    }

    if (!formData.department.trim()) {
      newErrors.department = '부서를 입력해주세요.';
    }

    if (!formData.position.trim()) {
      newErrors.position = '직위를 입력해주세요.';
    }

    if (!formData.role.trim()) {
      newErrors.role = '직책을 입력해주세요.';
    }

    if (!formData.appointmentPeriod.start) {
      newErrors.startDate = '발령 시작일을 선택해주세요.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 폼 제출 핸들러
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      let userId: string;
      
      if (isEdit) {
        await updateUser(editUser.id, formData);
        userId = editUser.id;
      } else {
        userId = await createUser(formData);
      }

      // 사진 업로드가 있는 경우
      let photoInfo = editUser?.photo || { url: null, fileName: null, uploadedAt: null };
      
      if (photoFile) {
        const uploadResult = await uploadFile(photoFile, userId, 'photo');
        photoInfo = uploadResult;
        
        // 사용자 문서에 사진 정보 업데이트
        await updateUserFile(userId, 'photo', uploadResult);
      }

      // 사용자 정보 객체 생성
      const userRecord: UserRecord = {
        id: userId,
        ...formData,
        photo: photoInfo,
        appointmentPeriod: {
          start: formData.appointmentPeriod.start as any,
          end: formData.appointmentPeriod.end as any || null
        },
        initialEducationDate: formData.initialEducationDate as any,
        recentEducationDate: formData.recentEducationDate as any,
        initialEducationCert: editUser?.initialEducationCert || { url: null, fileName: null, uploadedAt: null },
        recentEducationCert: editUser?.recentEducationCert || { url: null, fileName: null, uploadedAt: null },
        createdAt: editUser?.createdAt || new Date() as any,
        updatedAt: new Date() as any,
        createdBy: editUser?.createdBy || '',
        updatedBy: ''
      };

      if (!isEdit) {
        // 신규 사용자인 경우: 즉시 성공 처리하고 폼 닫기
        onSuccess(userRecord);
        handleClose();
        // 폼 상태 초기화
        setFormData({
          headquarters: '',
          department: '',
          position: '',
          role: '',
          assignments: [],
          location: '',
          name: '',
          email: '',
          employeeId: '',
          appointmentPeriod: {
            start: new Date(),
            end: null
          },
          remarks: '',
          initialEducationDate: null,
          recentEducationDate: null
        });
        setPhotoFile(null);
        setPhotoPreview(null);
        setTempUser(null);
      } else {
        // 기존 사용자 편집인 경우: 즉시 성공 처리
        onSuccess(userRecord);
        handleClose();
      }
    } catch (error: any) {
      handleError(error, isEdit ? '인원 정보 수정' : '인원 추가');
    } finally {
      setLoading(false);
    }
  };

  // 문서 업로드 성공 핸들러
  const handleDocumentUploadSuccess = async (updatedFile: any) => {
    console.log('문서 업로드 성공:', updatedFile);
    
    if (editUser) {
      // 편집 모드인 경우 - formData에도 반영해야 함
      setFormData(prev => ({
        ...prev,
        [updatedFile.documentType === 'initialEducationCert' ? 'initialEducationCert' : 'recentEducationCert']: updatedFile
      }));

      // 상위 컴포넌트에 변경사항 즉시 알리기 (사용자 목록 새로고침용)
      try {
        console.log('파일 변경 후 최신 사용자 정보 가져오는 중...', editUser.id);
        const updatedUser = await getUserById(editUser.id);
        if (updatedUser) {
          console.log('최신 사용자 정보 가져옴:', updatedUser);
          // 즉시 상위 컴포넌트에 알림
          onSuccess(updatedUser);
          console.log('onSuccess 호출 완료');
        }
      } catch (error) {
        console.warn('업데이트된 사용자 정보 가져오기 실패:', error);
        // 실패해도 현재 정보로 업데이트
        onSuccess(editUser);
      }
    } else if (tempUser) {
      // 신규 사용자인 경우 - tempUser에 반영하고 즉시 성공 처리
      const updatedTempUser = {
        ...tempUser,
        [updatedFile.documentType === 'initialEducationCert' ? 'initialEducationCert' : 'recentEducationCert']: updatedFile
      };
      setTempUser(updatedTempUser);
      
      // 즉시 상위 컴포넌트에 알림
      onSuccess(updatedTempUser);
      
      // 폼 닫기 및 상태 초기화
      handleClose();
      setTempUser(null);
    }
    
    console.log('문서 업로드 후 상태 업데이트 완료');
  };

  // 문서 삭제 성공 핸들러
  const handleDocumentDeleteSuccess = async (documentType: 'initialEducationCert' | 'recentEducationCert') => {
    console.log('문서 삭제 성공:', documentType);
    
    // 편집 모드인 경우 formData에서도 제거
    if (editUser) {
      setFormData(prev => ({
        ...prev,
        [documentType]: {
          url: null,
          fileName: null,
          uploadedAt: null
        }
      }));

      // 상위 컴포넌트에 변경사항 알리기 (사용자 목록 새로고침용)
      try {
        console.log('파일 변경 후 최신 사용자 정보 가져오는 중...', editUser.id);
        const updatedUser = await getUserById(editUser.id);
        if (updatedUser) {
          console.log('최신 사용자 정보 가져옴:', updatedUser);
          onSuccess(updatedUser);
          console.log('onSuccess 호출 완료');
        }
      } catch (error) {
        console.warn('업데이트된 사용자 정보 가져오기 실패:', error);
      }
    } else if (tempUser) {
      // 신규 생성 모드인 경우 tempUser에서도 제거
      setTempUser(prev => prev ? {
        ...prev,
        [documentType]: {
          url: null,
          fileName: null,
          uploadedAt: null
        }
      } : prev);
    }
    
    console.log('문서 삭제 후 상태 업데이트 완료');
  };

  // 기본 정보만 저장하고 계속 (신규 등록 완료)
  const handleSaveAndContinue = () => {
    if (tempUser) {
      onSuccess(tempUser);
      handleClose();
    }
  };

  // 다이얼로그 닫기
  const handleClose = () => {
    setFormData({
      headquarters: '',
      department: '',
      position: '',
      role: '',
      assignments: [],
      location: '',
      name: '',
      email: '',
      employeeId: '',
      appointmentPeriod: {
        start: new Date(),
        end: null
      },
      remarks: '',
      initialEducationDate: null,
      recentEducationDate: null
    });
    setPhotoFile(null);
    setPhotoPreview(null);
    setErrors({});
    setTempUser(null);
    onClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="body"
      >
        <DialogTitle>
          <Box display="flex" justifyContent="between" alignItems="center">
            <Typography variant="h6">
              {isEdit ? '인원 정보 수정' : '새 인원 추가'}
            </Typography>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            {/* 프로필 사진 */}
            <Box sx={{ minWidth: { md: 200 } }}>
              <ImageUpload
                currentImageUrl={photoPreview}
                userName={formData.name}
                onImageSelect={handleImageSelect}
                disabled={loading}
              />
              {errors.photo && (
                <Typography color="error" variant="caption" textAlign="center" display="block" mt={1}>
                  {errors.photo}
                </Typography>
              )}
            </Box>

            {/* 기본 정보 */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <TextField
                  fullWidth
                  label="이름 *"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name}
                />
                <TextField
                  fullWidth
                  label="이메일 *"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email || "로그인에 사용될 이메일 주소"}
                  placeholder="user@example.com"
                />
                <TextField
                  fullWidth
                  label="사번 *"
                  value={formData.employeeId}
                  onChange={(e) => handleInputChange('employeeId', e.target.value)}
                  error={!!errors.employeeId}
                  helperText={errors.employeeId}
                />
                <TextField
                  fullWidth
                  label="본부 *"
                  value={formData.headquarters}
                  onChange={(e) => handleInputChange('headquarters', e.target.value)}
                  error={!!errors.headquarters}
                  helperText={errors.headquarters}
                />
                <TextField
                  fullWidth
                  label="부서 *"
                  value={formData.department}
                  onChange={(e) => handleInputChange('department', e.target.value)}
                  error={!!errors.department}
                  helperText={errors.department}
                />
                <TextField
                  fullWidth
                  label="직위 *"
                  value={formData.position}
                  onChange={(e) => handleInputChange('position', e.target.value)}
                  error={!!errors.position}
                  helperText={errors.position}
                />
                <TextField
                  fullWidth
                  label="직책 *"
                  value={formData.role}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                  error={!!errors.role}
                  helperText={errors.role}
                />
                <TextField
                  fullWidth
                  label="지점/공항"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  error={!!errors.location}
                  helperText={errors.location}
                  placeholder="근무 지점 또는 공항을 입력하세요"
                />
              </Box>

              {/* 발령 사항 (다중 선택) */}
              <Box sx={{ mt: 2 }}>
                <Autocomplete
                  multiple
                  options={ASSIGNMENT_OPTIONS}
                  value={formData.assignments}
                  onChange={(event, newValue) => {
                    handleInputChange('assignments', newValue);
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                        key={option}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="발령 사항"
                      placeholder="발령 사항을 선택하세요"
                      error={!!errors.assignments}
                      helperText={errors.assignments || "여러 개 선택 가능"}
                    />
                  )}
                />
              </Box>
            </Box>
          </Box>

          {/* 발령기간 */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              발령기간
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <DatePicker
                label="시작일 *"
                value={formData.appointmentPeriod.start}
                onChange={(date) => handleDateChange('start', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.startDate,
                    helperText: errors.startDate
                  }
                }}
              />
              <DatePicker
                label="종료일 (선택사항)"
                value={formData.appointmentPeriod.end}
                onChange={(date) => handleDateChange('end', date)}
                slotProps={{
                  textField: {
                    fullWidth: true
                  }
                }}
              />
            </Box>
          </Box>

          {/* 보안교육 이수일 */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              보안교육 이수일
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <DatePicker
                label="초기 보안교육 이수일"
                value={formData.initialEducationDate}
                onChange={(date) => handleInputChange('initialEducationDate', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: "실제 교육을 이수한 날짜를 입력하세요"
                  }
                }}
              />
              <DatePicker
                label="정기 보안교육 이수일"
                value={formData.recentEducationDate}
                onChange={(date) => handleInputChange('recentEducationDate', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: "최근 정기 교육을 이수한 날짜를 입력하세요"
                  }
                }}
              />
            </Box>
          </Box>

          {/* 비고 */}
          <Box sx={{ mt: 3 }}>
            <TextField
              fullWidth
              label="비고"
              multiline
              rows={3}
              value={formData.remarks}
              onChange={(e) => handleInputChange('remarks', e.target.value)}
              placeholder="추가 정보나 특이사항을 입력하세요..."
            />
          </Box>

          {/* 교육이수증 업로드 (기존 사용자이거나 임시 저장된 사용자인 경우에만 표시) */}
          {(isEdit || tempUser) && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                교육이수증 관리
              </Typography>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mt: 2 }}>
                <DocumentUpload
                  userId={(editUser?.id || tempUser?.id) || ''}
                  documentType="initialEducationCert"
                  currentDocument={editUser?.initialEducationCert || tempUser?.initialEducationCert}
                  title="초기 교육이수증"
                  description="신규 인원의 초기 보안교육 이수증"
                  onUploadSuccess={handleDocumentUploadSuccess}
                  onDeleteSuccess={() => handleDocumentDeleteSuccess('initialEducationCert')}
                  disabled={loading}
                />

                <DocumentUpload
                  userId={(editUser?.id || tempUser?.id) || ''}
                  documentType="recentEducationCert"
                  currentDocument={editUser?.recentEducationCert || tempUser?.recentEducationCert}
                  title="정기 교육이수증"
                  description="정기 보안교육 이수증 (최신)"
                  onUploadSuccess={handleDocumentUploadSuccess}
                  onDeleteSuccess={() => handleDocumentDeleteSuccess('recentEducationCert')}
                  disabled={loading}
                />
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            취소
          </Button>
          
          {tempUser && !isEdit && (
            <Button
              onClick={handleSaveAndContinue}
              variant="outlined"
              disabled={loading}
            >
              나중에 문서 업로드
            </Button>
          )}
          
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {tempUser && !isEdit ? '기본 정보 저장 완료' : isEdit ? '수정' : '추가'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default UserForm;
